import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Menu } from "@headlessui/react";
import { EllipsisHorizontalIcon } from "@heroicons/react/24/outline";
import { MapPinIcon } from "@heroicons/react/24/solid";
import classNames from "classnames";

import DropWaterIcon from "@components/icons/DropWaterIcon";
import Loading from "@components/shared/Loading";
import WeatherIcon, { conditionGlyph } from "@components/icons/WeatherIcon";
import Card from "@components/zenith/ui/Card";
import { useGetWeather } from "@hooks/query/zenith/useGetWeather";
import {
  toWeatherCell,
  type WeatherPlace,
  type WeatherReport,
} from "@services/zenith/weather";
import type { WeatherLocation } from "./subscriberLocations";
import { useSubscriberLocations } from "./useSubscriberLocations";

// The humidity gauge's semicircle: radius 30, so its length is 30π.
const ARC = "M8 40 A30 30 0 0 1 68 40";
const ARC_LENGTH = Math.PI * 30;

const HERE = "here";

/** The weather at the subscriber location, or another weather location the subscriber picks. */
const WeatherWidget: React.FunctionComponent = () => {
  const { t, i18n } = useTranslation();
  const { locations, isError: isLocationsError } = useSubscriberLocations();
  // The device's position, once the subscriber picks "My current location".
  const [herePlace, setHerePlace] = useState<WeatherPlace>();
  const [hereRefused, setHereRefused] = useState(false);
  const here = herePlace && {
    id: HERE,
    name: t("zenith.dashboard.weather.current_location"),
    place: herePlace,
  };
  // Falls back to the subscriber location when the choice isn't among the locations.
  const [chosenId, setChosenId] = useState<string>();
  const location =
    (chosenId === HERE ? here : locations?.find(l => l.id === chosenId)) ??
    locations?.[0];

  const choose = (id: string) => {
    setChosenId(id);
    setHereRefused(false);
  };
  // Asked only when picked, so opening My Dashboard never prompts for location.
  const chooseHere = () =>
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        // No finer position than the API's cell leaves the browser.
        setHerePlace({
          lat: toWeatherCell(coords.latitude),
          lng: toWeatherCell(coords.longitude),
        });
        choose(HERE);
      },
      () => setHereRefused(true),
      { timeout: 10_000, maximumAge: 10 * 60_000 },
    );

  const lang = i18n.resolvedLanguage ?? "en";
  const { data: weather, isError: isWeatherError } = useGetWeather(
    location && { ...location.place, lang },
  );

  const notice = (text: string) => (
    <p className="px-5 py-4 text-sm text-gray-500">{text}</p>
  );

  return (
    <Card
      // The location menu drops below the header.
      className="overflow-visible"
      action={
        locations && location ? (
          <LocationMenu
            locations={locations}
            current={location}
            onChoose={choose}
            onChooseHere={"geolocation" in navigator ? chooseHere : undefined}
          />
        ) : null
      }
      title={
        <span className="flex items-center gap-2.5">
          <WeatherIcon
            glyph="cloud-drizzle-fill"
            className="h-5 w-5 text-primary-1"
          />
          {t("zenith.dashboard.weather.title")}
        </span>
      }
    >
      {/* A failed refresh leaves the last report up: it shows its own time. */}
      {weather && location ? (
        <Report weather={weather} location={location} lang={lang} />
      ) : isWeatherError || isLocationsError ? (
        notice(t("zenith.dashboard.weather.unavailable"))
      ) : (
        <Loading />
      )}
      <p role="status" className="px-4 pb-3 text-xs text-gray-500 empty:hidden">
        {hereRefused
          ? t("zenith.dashboard.weather.current_location_unavailable")
          : null}
      </p>
    </Card>
  );
};

const LocationMenu: React.FunctionComponent<{
  locations: WeatherLocation[];
  current: WeatherLocation;
  onChoose: (id: string) => void;
  /** Absent where the browser can't locate the device. */
  onChooseHere?: () => void;
}> = ({ locations, current, onChoose, onChooseHere }) => {
  const { t } = useTranslation();
  const label = t("zenith.dashboard.weather.change_location");

  return (
    <Menu as="div" className="relative">
      <Menu.Button className="block rounded p-1 text-gray-500 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-1">
        <span className="sr-only">{label}</span>
        <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
      </Menu.Button>
      <Menu.Items
        aria-label={label}
        className="absolute right-0 z-10 mt-2 max-h-72 w-60 origin-top-right overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg focus:outline-none"
      >
        <div
          aria-hidden="true"
          className="border-b border-gray-200 px-4 py-2 text-xs font-bold uppercase tracking-widest text-gray-500"
        >
          {label}
        </div>
        {[
          ...locations,
          ...(onChooseHere
            ? [
                {
                  id: HERE,
                  name: t("zenith.dashboard.weather.current_location"),
                },
              ]
            : []),
        ].map(location => {
          const isCurrent = location.id === current.id;
          return (
            <Menu.Item key={location.id}>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() =>
                    location.id === HERE
                      ? onChooseHere!()
                      : onChoose(location.id)
                  }
                  aria-current={isCurrent ? "true" : undefined}
                  className={classNames(
                    "flex w-full items-center gap-2 px-4 py-2 text-left text-sm",
                    { "bg-gray-50": active },
                    isCurrent
                      ? "font-semibold text-primary-1"
                      : "text-gray-900",
                  )}
                >
                  <MapPinIcon
                    className="h-3.5 w-3.5 shrink-0 opacity-70"
                    aria-hidden="true"
                  />
                  <span className="truncate">
                    {location.name || t("zenith.dashboard.weather.address")}
                  </span>
                </button>
              )}
            </Menu.Item>
          );
        })}
      </Menu.Items>
    </Menu>
  );
};

const Report: React.FunctionComponent<{
  weather: WeatherReport;
  location: WeatherLocation;
  lang: string;
}> = ({
  weather: { utcOffsetSeconds, current, today, daily },
  location,
  lang,
}) => {
  const { t } = useTranslation();
  // The place's own day and time, wherever the subscriber is: its clock, read in UTC.
  const observed = (options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(lang, { ...options, timeZone: "UTC" }).format(
      new Date(Date.parse(current.observedAt) + utcOffsetSeconds * 1000),
    );
  const degrees = (temp: number) => (
    <span className="text-lg font-semibold">
      {temp}
      <span className="align-super text-xs">°</span>
    </span>
  );

  return (
    <div className="p-4">
      <div className="flex flex-col gap-4 rounded-xl bg-night-sky px-5 py-4 text-white">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-base font-bold">
              {observed({ weekday: "long" })}
            </div>
            <div className="flex items-start gap-0.5">
              <span className="text-6xl font-bold leading-none">
                {current.temp}
              </span>
              <span className="mt-1.5 text-lg">°</span>
            </div>
            <div className="mt-1 text-xs opacity-75">
              {t("zenith.dashboard.weather.last_updated", {
                time: observed({
                  hour: "2-digit",
                  minute: "2-digit",
                  hourCycle: "h23",
                }),
              })}
            </div>
          </div>
          <div className="flex flex-col items-end">
            <div className="mb-1.5 text-base opacity-90 first-letter:uppercase">
              {current.description}
            </div>
            <WeatherIcon
              glyph={conditionGlyph(current.icon)}
              className="h-12 w-12"
            />
          </div>
        </div>

        <div className="flex items-center justify-between">
          {/* A table, so the label column fits the longest label in any language. */}
          <dl className="table border-separate border-spacing-y-1">
            <div className="table-row">
              <dt className="table-cell pr-3 text-base opacity-80">
                {t("zenith.dashboard.weather.high")}
              </dt>
              <dd className="table-cell">{degrees(today.high)}</dd>
            </div>
            <div className="table-row">
              <dt className="table-cell pr-3 text-base opacity-80">
                {t("zenith.dashboard.weather.low")}
              </dt>
              <dd className="table-cell">{degrees(today.low)}</dd>
            </div>
          </dl>
          <div
            role="meter"
            aria-label={t("zenith.dashboard.weather.humidity") as string}
            aria-valuenow={current.humidity}
            aria-valuemin={0}
            aria-valuemax={100}
            className="flex flex-col items-center gap-0.5"
          >
            <div className="relative">
              <svg
                width="76"
                height="46"
                viewBox="0 0 76 46"
                aria-hidden="true"
              >
                <path
                  d={ARC}
                  fill="none"
                  strokeWidth="7"
                  strokeLinecap="round"
                  className="stroke-white/30"
                />
                <path
                  d={ARC}
                  fill="none"
                  strokeWidth="7"
                  strokeLinecap="round"
                  strokeDasharray={`${ARC_LENGTH * (current.humidity / 100)} 200`}
                  className="stroke-white transition-all duration-500"
                />
              </svg>
              <DropWaterIcon className="absolute bottom-2 left-1/2 h-4 w-4 -translate-x-1/2 text-sky-400" />
            </div>
            <div className="text-sm font-semibold">{current.humidity}%</div>
          </div>
        </div>

        <ul
          aria-label={t("zenith.dashboard.weather.outlook") as string}
          className="grid auto-cols-fr grid-flow-col gap-1"
        >
          {daily.map(day => {
            // A calendar date, read in UTC so the viewer's timezone can't shift it a day.
            const weekday = (weekday: "long" | "short") =>
              new Intl.DateTimeFormat(lang, {
                weekday,
                timeZone: "UTC",
              }).format(new Date(day.date));
            return (
              <li key={day.date} className="flex flex-col items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className="text-xs font-bold uppercase tracking-wide"
                >
                  {weekday("short")}
                </span>
                <WeatherIcon
                  glyph={conditionGlyph(day.icon)}
                  className="h-5 w-5"
                />
                <span className="sr-only">
                  {t("zenith.dashboard.weather.day_outlook", {
                    day: weekday("long"),
                    description: day.description,
                    high: day.high,
                    low: day.low,
                  })}
                </span>
              </li>
            );
          })}
        </ul>

        <div className="flex items-center justify-between gap-3 pt-1">
          <div className="flex min-w-0 items-center gap-1.5 text-xs opacity-90">
            <MapPinIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="truncate">
              {location.name || t("zenith.dashboard.weather.address")}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2 text-sm">
            <WeatherIcon glyph="wind" className="h-4 w-4" />
            <span className="opacity-90">
              {t("zenith.dashboard.weather.wind_speed")}
            </span>
            <span className="font-semibold">
              {new Intl.NumberFormat(lang, {
                style: "unit",
                unit: "kilometer-per-hour",
              }).format(current.windKmh)}
            </span>
          </div>
        </div>
      </div>
      <a
        href="https://openweathermap.org/"
        target="_blank"
        rel="noreferrer"
        className="mt-2 inline-block text-xs text-gray-500 hover:underline"
      >
        {t("zenith.dashboard.weather.attribution")}
      </a>
    </div>
  );
};

export default WeatherWidget;
